/** @format */

import {CommonChartData} from 'components/charts/multiline-chart/model/charts';
import {Widget} from 'components/PropertyWidget/model';
import {
  TooltipData,
  TooltipExtraRow,
  TooltipRow,
} from 'components/charts/multiline-chart/model/tooltip';

import {divideBySpaces} from 'helpers/numberHelpers';

import {ChartIds} from '../../models/chartIds';
import getTooltipPointForecast from './getTooltipPointForecast';
import getTooltipPointPlan from './getTooltipPointPlan';
import getValueWithToken from '../../helpers/getValueWithToken';
import {ALL_SENSOR_CHART_IDS} from '../../constants/allChartIds';
import getTooltipPointSensor from './getTooltipPointSensor';
import {ComplexValue} from '../../models/common';
import {SensorsList} from '../../helpers/sensors/types';
import {getHeaderDate} from './getHeaderDate';
import {getTooltipDelta} from './getTooltipDelta';

export interface MapTooltipPointOptions {
  chartId: ChartIds;
  // текущая дата
  date: string;
  // предыдущая дата в массиве дат
  previousDate?: string;
  // находимся ли мы в режиме дискретности (week, month)
  isDiscreteness: boolean;
  // начало графика (нужно для правильного вычисления периода в шапке)
  startDate: string;
  // вчерашняя дата
  yesterday?: string;
  // текущие данные графика
  chartData: CommonChartData;
  // данные графика из предыдущего пойнта (если есть, значит нужно отрендерить дельту)
  previousChartData?: CommonChartData;
  sensors?: SensorsList;
  widget: Widget<ComplexValue>;
}

const mapTooltipPoint = ({
  date,
  previousDate,
  yesterday,
  chartData,
  previousChartData,
  sensors,
  widget,
  chartId,
  startDate,
  isDiscreteness,
}: MapTooltipPointOptions): TooltipData<ChartIds> => {
  // Дополнительные строки (план, прогноз, сенсоры)

  const isSensor = ALL_SENSOR_CHART_IDS.includes(chartId);

  const tooltipPointPlan = isSensor
    ? []
    : getTooltipPointPlan({
        data: chartData,
        token: widget.token,
        roundNumberBy: widget.roundNumberBy,
      });

  const tooltipPointForecast = isSensor
    ? []
    : getTooltipPointForecast({
        data: chartData,
        token: widget.token,
        roundNumberBy: widget.roundNumberBy,
        hideForecast: yesterday === date, // не нужно показывать костыльный прогноз за вчера
      });

  const tooltipPointSensor =
    isSensor && sensors ? getTooltipPointSensor(chartId, sensors[date]) : [];

  const extraRows: TooltipExtraRow[] = [
    ...tooltipPointPlan,
    ...tooltipPointForecast,
    ...tooltipPointSensor,
  ];

  // Основные данные: дата, лейбл, значения

  const valuePrimary = chartData.primary ? divideBySpaces(chartData.primary) : undefined;
  const valueSecondary = chartData.secondary ? divideBySpaces(chartData.secondary) : undefined;
  const primary = valuePrimary
    ? getValueWithToken({
        value: valuePrimary,
        token: widget.token,
      })
    : '';
  const secondary = valueSecondary
    ? getValueWithToken({
        value: valueSecondary,
        token: widget.token,
      })
    : '';

  // Дельта

  const delta = getTooltipDelta({
    current: chartData,
    previous: previousChartData,
    roundNumberBy: widget.roundNumberBy,
    token: widget.token,
    isPositiveRed: widget.isPositiveRed,
  });

  // Дата в шапке

  const {dateToDisplay, periodDays} = getHeaderDate({
    currentDate: date,
    previousDate,
    startDate,
    isDiscreteness,
  });

  // Записываем результат

  const rows: TooltipRow<ChartIds>[] = [
    {
      chartId,
      label: widget.label,
      value: {
        primary,
      },
    },
  ];

  if (secondary) {
    rows[0].value.secondary = secondary;
  }

  if (extraRows.length) {
    rows[0].extraRows = extraRows;
  }

  if (delta) {
    rows[0].delta = delta;
  }

  const result: TooltipData<ChartIds> = {
    date: dateToDisplay,
    rows,
  };

  if (periodDays) {
    result.periodDays = periodDays;
  }

  return result;
};

export default mapTooltipPoint;
